import { DragDropModule } from '@angular/cdk/drag-drop';
import { OVERLAY_DEFAULT_CONFIG, OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AddRecipientComponent } from 'add-recipient-lib';
import { AlertBannerComponent } from 'alert-banner-lib';
import { GoogleAnalyticsService, RumErrorHandler } from 'analytics-lib';
import { AuthInterceptor } from 'auth-service-lib';
import { AutoCompleteComponent } from 'auto-complete-lib';
import { ButtonToastComponent } from 'button-toast-lib';
import { BullhornRetryInterceptor } from 'datasource-service-lib';
import { DocumentTypeDropdownComponent } from 'document-type-dropdown-lib';
import { DocumentTypePickerComponent } from 'document-type-picker-lib';
import { EntityPickerComponent } from 'entity-picker-lib';
import { SalesforceEntityIconComponent } from 'entity-icon-lib';
import { EntityToggleGroupComponent } from 'entity-toggle-group-lib';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { FileTypeIconComponent } from 'filetype-icon-lib';
import { HamburgerComponent } from 'hamburger-lib';
import { InlineEditorComponent } from 'inline-editor-lib';
import { MultiSelectAutocompleteComponent } from 'multi-select-autocomplete-lib';
import { RippleDirective } from 'ripple-lib';
import { UserRestrictionsFilterComponent, UserRestrictionsPanelComponent } from 'user-restrictions-filter-lib';
import { VersionComponent } from 'version-lib';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChipsComponent } from './common-components/chips/chips.component';
import { DisplayStatusRestrictionComponent } from './common-components/display-status-restriction/display-status-restriction.component';
import { DialogConfirmComponent } from './common-components/dialog-confirm/dialog-confirm.component';
import { SkeletonComponent } from './common-components/skeleton/skeleton.component';
import { UploadFileComponent } from './common-components/upload-file/upload-file.component';
import { Adv2ConfigComponent } from './components/adv2-config/adv2-config.component';
import { DocumentOptionsTabComponent } from './components/adv2-config/document-options/document-options-tab.component';
import { FieldOptionsTabComponent } from './components/adv2-config/field-options/field-options-tab.component';
import { SalesforceMappingComponent } from './components/adv2-config/salesforce-mapping/salesforce-mapping.component';
import { SignatureTabComponent } from './components/adv2-config/signature/signature-tab.component';
import { SalesforcePathBuilderPickerComponent } from './components/adv2-config/salesforce-mapping/salesforce-path-builder-picker.component';
import { DocusignComponent } from './components/adv2-config/signature/docusign/docusign.component';
import { NoSignatureProviderComponent } from './components/adv2-config/signature/none/no-signature-provider.component';
import { HamburgerWrapperComponent } from './components/hamburger-wrapper/hamburger-wrapper.component';
import { AuthRedirectComponent } from './components/login/auth-redirect.component';
import { SignInComponent } from './components/login/sign-in.component';
import { AttachmentRestrictionsComponent } from './components/sections/attachment-section/attachment-restrictions/attachment-restrictions.component';
import { AttachmentSourcePickerComponent } from './components/sections/attachment-section/attachment-restrictions/attachment-source-restrictions/attachment-source-picker.component';
import { AttachmentSourceRestrictionComponent } from './components/sections/attachment-section/attachment-restrictions/attachment-source-restrictions/attachment-source-restrictions.component';
import { FileAgeRestrictionComponent } from './components/sections/attachment-section/attachment-restrictions/file-age-restriction/file-age-restriction.component';
import { FileTypeRestrictionsComponent } from './components/sections/attachment-section/attachment-restrictions/file-type-restrictions/file-type-restrictions.component';
import { AttachmentSectionComponent } from './components/sections/attachment-section/attachment-section.component';
import { UploadSourceSelectorComponent } from './components/sections/attachment-section/upload-source-selector/upload-source-selector.component';
import { DocxTemplateSectionComponent } from './components/sections/docx-template-section/docx-template-section.component';
import { HtmlDocxTemplateSectionComponent } from './components/sections/html-docx-template-section/html-docx-template-section.component';
import { MergeSectionComponent } from './components/sections/merge-section/merge-section.component';
import { OutputFileNameComponent } from './components/sections/output-file-name/output-file-name.component';
import { OutputFileTypeComponent } from './components/sections/output-file-type/output-file-type.component';
import { WorkflowConfigListViewComponent } from './components/workflow-config-list-view/workflow-config-list-view.component';
import { ActionsComponent } from './components/workflow-config/actions/actions.component';
import { BullhornCreateNoteComponent } from './components/workflow-config/actions/bullhorn-create-note/bullhorn-create-note.component';
import { SalesforceCreateCallComponent } from './components/workflow-config/actions/salesforce-create-call/salesforce-create-call.component';
import { TriggerAccordionComponent } from './components/workflow-config/actions/trigger-accordion/trigger-accordion.component';
import { UpdateFieldComponent } from './components/workflow-config/actions/update-field/update-field.component';
import { AwesomedocWorkflowConfigComponent } from './components/workflow-config/config/awesomedoc-config-component/awesomedoc-workflow-config.component';
import { DocmergeAddNewSectionComponent } from './components/workflow-config/config/docmerge-add-new-section/docmerge-add-new-section.component';
import { CertificationSectionComponent } from './components/workflow-config/config/docmerge-section-list/certification-section/certification-section.component';
import { DocmergeSectionListComponent } from './components/workflow-config/config/docmerge-section-list/docmerge-section-list.component';
import { DocmergeTemplateSectionComponent } from './components/workflow-config/config/docmerge-section-list/docmerge-template-section/docmerge-template-section.component';
import { DefaultTemplateComponent } from './components/workflow-config/config/docmerge-section-list/docmerge-template-section/default-template/default-template.component';
import { EditableModeToggleComponent } from './components/workflow-config/config/docmerge-section-list/docmerge-template-section/editable-mode-toggle/editable-mode-toggle.component';
import { EntityTypeDropdownComponent } from './components/workflow-config/config/docmerge-section-list/docmerge-template-section/entity-type-dropdown/entity-type-dropdown.component';
import { FileSectionComponent } from './components/workflow-config/config/docmerge-section-list/file-section/file-section.component';
import { FixedFileSectionComponent } from './components/workflow-config/config/docmerge-section-list/fixed-file-section/fixed-file-section.component';
import { DocmergeWorkflowConfigComponent } from './components/workflow-config/config/docmerge-workflow-config/docmerge-workflow-config.component';
import { WorkflowConfigComponent } from './components/workflow-config/config/workflow-config.component';
import { DocumentDestinationFileTypeComponent } from './components/workflow-config/document-destination-filetype/document-destination-filetype.component';
import { DocumentDestinationComponent } from './components/workflow-config/document-destination/document-destination.component';
import { DocusignTabComponent } from './components/workflow-config/docusign-tab/docusign-tab.component';
import { EntityTypePickerComponent } from './components/workflow-config/entity-type-picker/entity-type-picker.component';
import { ScriptingBoxComponent } from './components/workflow-config/scripting-box/scripting-box.component';
import { TemplateAvailabilityComponent } from './components/workflow-config/template-availability/template-availability.component';
import { BullhornEntityFiltersComponent } from './components/workflow-config/user-restrictions/bullhorn/entity-filters/bullhorn.entity-filters.component';
import { BullhornCompanyFiltersComponent } from './components/workflow-config/user-restrictions/bullhorn/entity-filters/company/bullhorn.company-filters.component';
import { BullhornPlacementFiltersComponent } from './components/workflow-config/user-restrictions/bullhorn/entity-filters/placement/bullhorn.placement-filters.component';
import { BullhornUserRestrictionsComponent } from './components/workflow-config/user-restrictions/bullhorn/user-restrictions/bullhorn.user-restrictions.component';
import { UserRestrictionsComponent } from './components/workflow-config/user-restrictions/user-restrictions.component';
import { SectionDirective } from './directives/section.directive';
import { HeaderComponent } from './header/header.component';
import { SF_MAPPING_ENTRIES } from 'attachment-source-lib';
import { SalesforceMappingStoreService } from './services/salesforce/salesforce-mapping-store.service';

@NgModule({
  declarations: [
    AppComponent,
    WorkflowConfigListViewComponent,
    WorkflowConfigComponent,
    DocmergeWorkflowConfigComponent,
    DocmergeAddNewSectionComponent,
    DocmergeSectionListComponent,
    FileSectionComponent,
    FixedFileSectionComponent,
    AwesomedocWorkflowConfigComponent,
    DocxTemplateSectionComponent,
    HeaderComponent,
    SectionDirective,
    SkeletonComponent,
    OutputFileNameComponent,
    AuthRedirectComponent,
    SignInComponent,
    DialogConfirmComponent,
    Adv2ConfigComponent,
    DocusignTabComponent,
    Adv2ConfigComponent,
    DocusignComponent,
    AttachmentSectionComponent,
    MergeSectionComponent,
    NoSignatureProviderComponent,
    EntityTypePickerComponent,
    SalesforceMappingComponent,
    OutputFileTypeComponent,
    UserRestrictionsComponent,
    BullhornUserRestrictionsComponent,
    BullhornEntityFiltersComponent,
    BullhornCompanyFiltersComponent,
    BullhornPlacementFiltersComponent,
    ScriptingBoxComponent,
    ChipsComponent,
    DisplayStatusRestrictionComponent,
    UploadFileComponent,
    HtmlDocxTemplateSectionComponent,
    ChipsComponent,
    TemplateAvailabilityComponent,
    DocumentDestinationComponent,
    ActionsComponent,
    BullhornCreateNoteComponent,
    SalesforceCreateCallComponent,
    TriggerAccordionComponent,
    UpdateFieldComponent,
    UploadSourceSelectorComponent,
    FileTypeRestrictionsComponent,
    FileAgeRestrictionComponent,
    AttachmentRestrictionsComponent,
    AttachmentSourceRestrictionComponent,
    AttachmentSourcePickerComponent,
    HamburgerWrapperComponent,
    DocmergeTemplateSectionComponent,
    EntityTypeDropdownComponent,
    CertificationSectionComponent,
    DocumentDestinationFileTypeComponent,
    FieldOptionsTabComponent,
    SignatureTabComponent,
    DocumentOptionsTabComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MatTableModule,
    MatSortModule,
    BrowserAnimationsModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatRadioModule,
    MatCheckboxModule,
    FormsModule,
    MatButtonToggleModule,
    MatButtonModule,
    MatTooltipModule,
    MatTabsModule,
    MatSlideToggleModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    DragDropModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatIconModule,
    OverlayModule,
    AlertBannerComponent,
    ButtonToastComponent,
    DocumentTypePickerComponent,
    AutoCompleteComponent,
    InlineEditorComponent,
    HamburgerComponent,
    EntityPickerComponent,
    AddRecipientComponent,
    MatInputModule,
    MultiSelectAutocompleteComponent,
    EntityToggleGroupComponent,
    FileTypeIconComponent,
    VersionComponent,
    RippleDirective,
    SalesforceEntityIconComponent,
    SalesforcePathBuilderPickerComponent,
    DocumentTypeDropdownComponent,
    EditableModeToggleComponent,
    DefaultTemplateComponent,
    UserRestrictionsFilterComponent,
    UserRestrictionsPanelComponent,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BullhornRetryInterceptor, multi: true },
    {
      provide: SF_MAPPING_ENTRIES,
      useFactory: (store: SalesforceMappingStoreService) => store.getEnabledMappings(),
      deps: [SalesforceMappingStoreService],
    },
    {
      provide: ErrorHandler,
      useClass: RumErrorHandler,
    },
    GoogleAnalyticsService,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: ENVIRONMENT, useValue: environment },
    { provide: RUNTIME_ENVIRONMENT, useValue: undefined }
  ],
  exports: [FileTypeRestrictionsComponent, FileAgeRestrictionComponent, EntityPickerComponent, CertificationSectionComponent],
})
export class AppModule {}
